import {
  aggregateByQuarter,
  effectiveDomesticSegmentPassengers,
  effectiveInternationalFacilityPassengers,
  legAirExciseBreakdown,
} from "./calculator";
import {
  effectiveFlightHours,
  effectiveTexasDepartures,
  type TexasLandingOptions,
  fuelCostUsd,
  personalFuelOwed2x,
  resolvedFuelGallons,
} from "./fuelDerived";
import { money } from "./money";
import type { FlightLeg, Rates } from "./types";

export type PeriodSelection =
  | { mode: "all" }
  | { mode: "year"; year: number }
  | { mode: "quarter"; year: number; quarter: 1 | 2 | 3 | 4 }
  | { mode: "month"; year: number; month: number }
  /** Jan 1–selected year through today (when year is current) or full prior calendar year. */
  | { mode: "ytd"; year: number }
  /** Jan 1 through last day of the given calendar month (same year) — Maintenance summary column. */
  | { mode: "janThroughMonth"; year: number; month: number };

/** Last calendar day of `month` (1–12) in `year`, as YYYY-MM-DD local. */
export function lastIsoDayOfCalendarMonth(year: number, month: number): string {
  return toIsoDateLocal(new Date(year, month, 0));
}

function ym(d: string): { y: number; m: number } {
  const [ys, ms] = d.split("-");
  return { y: parseInt(ys!, 10), m: parseInt(ms!, 10) };
}

export function toIsoDateLocal(d: Date): string {
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  return `${y}-${String(m).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** Newest flight date first; stable tie-break by route (descending sort key). */
export function pickLastNLegsByDate(legs: FlightLeg[], n: number): FlightLeg[] {
  if (!Number.isFinite(n) || n <= 0 || legs.length === 0) return [];
  const sorted = [...legs].sort((a, b) => {
    const c = b.flightDate.localeCompare(a.flightDate);
    if (c !== 0) return c;
    return `${b.fromIcao}|${b.toIcao}`.localeCompare(`${a.fromIcao}|${a.toIcao}`);
  });
  return sorted.slice(0, Math.min(n, sorted.length));
}

export type PeriodFilterOptions = { asOf?: Date };

export function filterLegsByPeriod(
  legs: FlightLeg[],
  p: PeriodSelection,
  opts?: PeriodFilterOptions
): FlightLeg[] {
  if (p.mode === "all") return [...legs];
  const asOf = opts?.asOf ?? new Date();
  const asOfStr = toIsoDateLocal(asOf);
  return legs.filter((leg) => {
    const { y, m } = ym(leg.flightDate);
    if (p.mode === "year") return y === p.year;
    if (p.mode === "month") return y === p.year && m === p.month;
    if (p.mode === "quarter") {
      const q = Math.floor((m - 1) / 3) + 1;
      return y === p.year && q === p.quarter;
    }
    if (p.mode === "ytd") {
      const start = `${p.year}-01-01`;
      if (p.year > asOf.getFullYear()) return false;
      const end = p.year < asOf.getFullYear() ? `${p.year}-12-31` : asOfStr;
      return leg.flightDate >= start && leg.flightDate <= end;
    }
    if (p.mode === "janThroughMonth") {
      const start = `${p.year}-01-01`;
      const end = lastIsoDayOfCalendarMonth(p.year, p.month);
      return leg.flightDate >= start && leg.flightDate <= end;
    }
    return true;
  });
}

/** Distinct calendar months (YYYY-MM) in legs — for “all time” flat fee months. */
export function distinctYearMonths(legs: FlightLeg[]): number {
  const s = new Set<string>();
  for (const leg of legs) {
    const { y, m } = ym(leg.flightDate);
    if (y && m) s.add(`${y}-${String(m).padStart(2, "0")}`);
  }
  return s.size;
}

export function flatFeeMonthsInPeriod(
  legs: FlightLeg[],
  p: PeriodSelection,
  monthlyFlatUsd: number,
  opts?: PeriodFilterOptions
): { months: number; flatTotal: number } {
  const asOf = opts?.asOf ?? new Date();
  let months = 1;
  if (p.mode === "all") {
    months = Math.max(1, distinctYearMonths(legs));
  } else if (p.mode === "year") {
    months = 12;
  } else if (p.mode === "quarter") {
    months = 3;
  } else if (p.mode === "month") {
    months = 1;
  } else if (p.mode === "ytd") {
    if (p.year > asOf.getFullYear()) months = 0;
    else if (p.year < asOf.getFullYear()) months = 12;
    else months = asOf.getMonth() + 1;
  } else if (p.mode === "janThroughMonth") {
    months = p.month;
  }
  return { months, flatTotal: money(months * monthlyFlatUsd) };
}

export type TimeshareRollup = {
  legCount: number;
  totalHours: number;
  totalPassengers: number;
  tripsBusiness: number;
  tripsPersonal: number;
  pctBusiness: number | null;
  pctPersonal: number | null;
  fuelCostTotal: number;
  personalFuel2xTotal: number;
  /** Sum of takeoffs on legs that depart Texas (Section 21.055-style). */
  texasDepartureTotal: number;
};

export type TimeshareRollupOptions = {
  isPersonalLeg?: (leg: FlightLeg) => boolean;
  texasLanding?: TexasLandingOptions;
};

export function timeshareRollup(
  legs: FlightLeg[],
  rates: Rates,
  opts?: TimeshareRollupOptions
): TimeshareRollup {
  const isPersonal = opts?.isPersonalLeg ?? ((leg: FlightLeg) => leg.personalUse);
  const txOpts = opts?.texasLanding;
  let totalHours = 0;
  let totalPassengers = 0;
  let tripsBusiness = 0;
  let tripsPersonal = 0;
  let fuelCostTotal = 0;
  let personalFuel2xTotal = 0;
  let texasDepartureTotal = 0;

  for (const leg of legs) {
    const h = effectiveFlightHours(leg);
    if (h !== null) totalHours += h;
    totalPassengers += Math.max(0, leg.passengerCount);
    if (isPersonal(leg)) tripsPersonal += 1;
    else tripsBusiness += 1;
    const fc = fuelCostUsd(leg, rates);
    if (fc !== null) fuelCostTotal += fc;
    personalFuel2xTotal += personalFuelOwed2x(leg, rates, isPersonal(leg));
    texasDepartureTotal += effectiveTexasDepartures(leg, txOpts);
  }

  const n = legs.length;
  const pctBusiness = n > 0 ? (100 * tripsBusiness) / n : null;
  const pctPersonal = n > 0 ? (100 * tripsPersonal) / n : null;

  return {
    legCount: n,
    totalHours,
    totalPassengers,
    tripsBusiness,
    tripsPersonal,
    pctBusiness,
    pctPersonal,
    fuelCostTotal: money(fuelCostTotal),
    personalFuel2xTotal: money(personalFuel2xTotal),
    texasDepartureTotal,
  };
}

export type TripTaxLine = {
  date: string;
  from: string;
  to: string;
  /** Raw log / CSV <code>passenger_count</code>. */
  passengerCount: number;
  federalAirExcise: number;
  /** Pax used for IRS No. 26 domestic segment (<code>dom_pax</code> if set, else <code>passenger_count</code>; 0 on foreign–foreign unless <code>dom_pax</code>). */
  effectiveDomesticSegmentPax: number;
  /** <code>max(1, segments_domestic)</code> in the segment-tax formula. */
  domesticSegmentsForTax: number;
  /** <code>intl_pax</code> when the route is US ↔ foreign (else 0). */
  internationalFacilityPax: number;
  percentageTax: number;
  domesticSegmentTax: number;
  internationalFacilityTax: number;
  alaskaHawaiiDepartureTax: number;
};

export function perTripFederalLines(legs: FlightLeg[], rates: Rates): TripTaxLine[] {
  const sorted = [...legs].sort((a, b) => {
    const c = b.flightDate.localeCompare(a.flightDate);
    if (c !== 0) return c;
    return `${a.fromIcao}|${a.toIcao}`.localeCompare(`${b.fromIcao}|${b.toIcao}`);
  });
  return sorted.map((leg) => {
    const b = legAirExciseBreakdown(leg, rates);
    const domPax = leg.excludeFrom720 ? 0 : effectiveDomesticSegmentPassengers(leg);
    const segsForTax = leg.excludeFrom720 ? 0 : Math.max(1, leg.segmentsDomestic);
    return {
      date: leg.flightDate,
      from: leg.fromIcao,
      to: leg.toIcao,
      passengerCount: Math.max(0, leg.passengerCount),
      federalAirExcise: money(
        b.percentageTax +
          b.domesticSegmentTax +
          b.internationalFacilityTax +
          b.alaskaHawaiiDepartureTax
      ),
      effectiveDomesticSegmentPax: domPax,
      domesticSegmentsForTax: segsForTax,
      internationalFacilityPax: effectiveInternationalFacilityPassengers(leg),
      percentageTax: b.percentageTax,
      domesticSegmentTax: b.domesticSegmentTax,
      internationalFacilityTax: b.internationalFacilityTax,
      alaskaHawaiiDepartureTax: b.alaskaHawaiiDepartureTax,
    };
  });
}


export function quarterlyFromLegs(legs: FlightLeg[], rates: Rates) {
  return aggregateByQuarter(legs, rates);
}

export function totalLandings(legs: FlightLeg[]): number {
  let n = 0;
  for (const leg of legs) {
    const ld = leg.landings;
    n += ld != null && Number.isFinite(ld) && ld >= 0 ? Math.round(ld) : 1;
  }
  return n;
}

/** Sum of leg takeoffs (blank → 1). */
export function totalTakeoffs(legs: FlightLeg[]): number {
  let n = 0;
  for (const leg of legs) {
    const t = leg.takeoffs;
    n += t != null && Number.isFinite(t) && t >= 0 ? Math.round(t) : 1;
  }
  return n;
}

export function totalFuelGallons(legs: FlightLeg[], rates: Rates): number {
  let g = 0;
  for (const leg of legs) {
    const v = resolvedFuelGallons(leg, rates.fuelLbsPerGallon);
    if (v !== null) g += v;
  }
  return money(g);
}
